import PropTypes from 'prop-types';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { ProjectContext, UserContext } from '@core/context';
import useEnvInfo from '@core/hooks/useEnvInfo';
import useIntercom from '@core/hooks/useIntercom';
import { useSuperHubStore } from '@core/store';
import classy from '@core/utils/classy';
import CustomHtmlFooter from '@Hub/Footer/CustomHtmlFooter';

import HealthCheck from './HealthCheck';
import InactiveBanner from './InactiveBanner';
import StagingBanner from './StagingBanner';
import StagingDiscussionsBanner from './StagingDiscussionsBanner';
import classes from './style.module.scss';
import SuggestedEditsPreview from './SuggestedEditsPreview';

function AppFooter({ isDetachedProductionSite }) {
  const { isProd } = useEnvInfo();

  const { project } = useContext(ProjectContext);
  const isSuperHubAdmin = useSuperHubStore(s => s.user.role === 'admin');
  const [isStoreReady, isEndUser] = useSuperHubStore(s => [s.isReady, s.user.role === 'end-user']);
  const isActive = project?.is_active;
  const { pathname } = useLocation();
  const isPreview = pathname.includes('/preview') && pathname.includes('/suggested-edits/');
  const isDiscuss = pathname.startsWith('/discuss');
  const isStagingEnabled = project?.parent ? project.parent.flags.staging : project.flags.staging;

  // The project's customer Intercom is only for true end-users — never any
  // teammate role (admin, editor, viewer). SuperHub teammates use ReadMe's
  // internal Intercom via the SidebarNav `IntercomButton`, with the default
  // launcher hidden.
  //
  // ⚠️ Wait for the SuperHub store to hydrate before booting. The default
  // role is `'end-user'` (`SuperHub/index.ts:615`), so an admin/editor would
  // briefly look like an end-user on first render. `useIntercom` boots once
  // (early-return guard at `useIntercom/index.ts:74`) and ignores subsequent
  // role changes, so a premature boot would leave the launcher visible for
  // every teammate.
  const { is_god: isGod } = useContext(UserContext);
  const isEnabled = isStoreReady && isEndUser && (!isProd || !isGod);
  useIntercom({ isEnabled, useProjectIntercomId: true });

  return (
    <>
      <footer aria-label="Status banner" className={classy(classes.Footer, 'AppFooter rm-Banners')}>
        <HealthCheck planTrial={project.planTrial} />
        {!isSuperHubAdmin && <InactiveBanner isActive={isActive} />}
        {/* Present when viewing staged changes */}
        <StagingBanner isStage={!isDetachedProductionSite && isStagingEnabled} />
        {/* Present when viewing discussion pages on a staging site */}
        <StagingDiscussionsBanner isDiscuss={isDiscuss} isStage={!isDetachedProductionSite && isStagingEnabled} />
        <SuggestedEditsPreview isPreview={isPreview} />
      </footer>
      <CustomHtmlFooter />
    </>
  );
}

AppFooter.propTypes = {
  isDetachedProductionSite: PropTypes.bool,
};

export default AppFooter;
